/**
 * Models API endpoints.
 */

import apiClient from '../client';
import type {
  Model,
  ModelCreate,
  ModelUpdate,
  ModelStatus,
  ModelInteraction,
  ModelInteractionCreate,
  ModelStats,
  ModelStatusStats,
  QuantumClassicalComparison,
} from '@/types';

export interface ModelListResponse {
  models: Model[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export interface ModelInteractionListResponse {
  interactions: ModelInteraction[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

/**
 * Create a new model.
 */
export const createModel = async (data: ModelCreate): Promise<Model> => {
  const response = await apiClient.post<Model>('/models', data);
  return response.data;
};

/**
 * Get models list with pagination and filters.
 */
export const getModels = async (params?: {
  status?: ModelStatus;
  is_shared?: boolean;
  created_by_id?: string;
  source_account_id?: string;
  training_job_id?: string;
  page?: number;
  page_size?: number;
}): Promise<ModelListResponse> => {
  const response = await apiClient.get<ModelListResponse>('/models', { params });
  return response.data;
};

/**
 * Get model by ID.
 */
export const getModel = async (modelId: string): Promise<Model> => {
  const response = await apiClient.get<Model>(`/models/${modelId}`);
  return response.data;
};

/**
 * Update a model.
 */
export const updateModel = async (modelId: string, data: ModelUpdate): Promise<Model> => {
  const response = await apiClient.put<Model>(`/models/${modelId}`, data);
  return response.data;
};

/**
 * Delete a model.
 */
export const deleteModel = async (modelId: string): Promise<void> => {
  await apiClient.delete(`/models/${modelId}`);
};

/**
 * Create a model interaction (prediction/evaluation).
 */
export const createModelInteraction = async (
  modelId: string,
  data: ModelInteractionCreate
): Promise<ModelInteraction> => {
  const response = await apiClient.post<ModelInteraction>(`/models/${modelId}/interactions`, data);
  return response.data;
};

/**
 * Get model interactions.
 */
export const getModelInteractions = async (
  modelId: string,
  params?: {
    page?: number;
    page_size?: number;
    feedback_type?: string;
    prediction?: string;
    sort_by?: string;
    sort_order?: string;
  }
): Promise<ModelInteractionListResponse> => {
  const response = await apiClient.get<ModelInteractionListResponse>(
    `/models/${modelId}/interactions`,
    { params }
  );
  return response.data;
};

/**
 * Host/deploy a model.
 */
export const hostModel = async (modelId: string): Promise<Model> => {
  const response = await apiClient.post<Model>(`/models/${modelId}/host`);
  return response.data;
};

/**
 * Make a prediction with a model using file upload.
 * Sends the file bytes so the backend can run real inference (QML URL or local linear when weights fit).
 */
export const predictModel = async (
  modelId: string,
  file?: File | null,
  fileName?: string,
  fileType?: string,
  fileSize?: number
): Promise<ModelInteraction> => {
  const formData = new FormData();

  if (file) {
    formData.append('file', file);
  }
  if (fileName) {
    formData.append('file_name', fileName);
  }
  if (fileType) {
    formData.append('file_type', fileType);
  }
  if (fileSize !== undefined) {
    formData.append('file_size', fileSize.toString());
  }

  const response = await apiClient.post<ModelInteraction>(
    `/models/${modelId}/predict`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  );
  return response.data;
};

/**
 * Get model statistics.
 */
export const getModelStats = async (modelId: string): Promise<ModelStats> => {
  const response = await apiClient.get<ModelStats>(`/models/${modelId}/stats`);
  return response.data;
};

/**
 * Get model status statistics for the current account.
 */
export const getModelStatusStats = async (): Promise<ModelStatusStats> => {
  const response = await apiClient.get<ModelStatusStats>('/models/stats');
  return response.data;
};

/**
 * Get quantum vs classical model comparison.
 */
export const getModelComparison = async (modelId: string): Promise<QuantumClassicalComparison> => {
  const response = await apiClient.get<QuantumClassicalComparison>(`/models/${modelId}/comparison`);
  return response.data;
};

/**
 * Get recommendations for a classification.
 */
export interface ModelRecommendationsResponse {
  model_id: string;
  classification: string;
  recommendations: string[];
}

export const getRecommendations = async (
  modelId: string,
  classification: string
): Promise<ModelRecommendationsResponse> => {
  const response = await apiClient.get<ModelRecommendationsResponse>(
    `/models/${modelId}/recommendations`,
    { params: { classification } }
  );
  return response.data;
};

/**
 * Update model recommendations configuration.
 */
export const updateRecommendations = async (
  modelId: string,
  config: Record<string, string[]>
): Promise<Model> => {
  const response = await apiClient.put<Model>(`/models/${modelId}/recommendations`, config);
  return response.data;
};

/**
 * Update model reasoning setting.
 */
export const updateModelReasoning = async (
  modelId: string,
  enableReasoning: boolean
): Promise<Model> => {
  const response = await apiClient.patch<Model>(`/models/${modelId}/reasoning`, {
    enable_reasoning: enableReasoning,
  });
  return response.data;
};

/**
 * Update model interaction with feedback.
 */
export interface ModelInteractionFeedbackUpdate {
  user_feedback?: Record<string, unknown>;
  feedback_type?: 'accepted' | 'corrected' | 'rejected';
  recommendations_shown?: string[];
}

/**
 * Chat with AI about a model.
 */
export interface ModelChatRequest {
  message: string;
}

export interface ModelChatResponse {
  message: string;
  insights?: string[] | null;
  recommendations?: string[] | null;
  questions?: {
    question: string;
    answer: string;
  }[] | null;
}

export const chatWithModel = async (
  modelId: string,
  message: string
): Promise<ModelChatResponse> => {
  const response = await apiClient.post<ModelChatResponse>(
    `/models/${modelId}/chat`,
    { message }
  );
  return response.data;
};

export const updateInteractionFeedback = async (
  interactionId: string,
  data: ModelInteractionFeedbackUpdate
): Promise<ModelInteraction> => {
  const response = await apiClient.put<ModelInteraction>(
    `/models/interactions/${interactionId}/feedback`,
    data
  );
  return response.data;
};

/**
 * Get unique creators of models visible to the current account.
 */
export const getModelCreators = async (): Promise<{ id: string; name: string }[]> => {
  const response = await apiClient.get<{ id: string; name: string }[]>('/models/creators');
  return response.data;
};

/**
 * Explain model chart using AI.
 */
export interface ChartExplanationRequest {
  chart_type: string;
  chart_data: Record<string, unknown>;
  metric_values?: Record<string, unknown>;
}

export interface ChartExplanationResponse {
  explanation: string;
  summary?: string;
  verdict?: string;
  next_steps?: string[];
  questions?: {
    question: string;
    answer: string;
  }[];
}

export const explainModelChart = async (
  modelId: string,
  data: ChartExplanationRequest
): Promise<ChartExplanationResponse> => {
  const response = await apiClient.post<ChartExplanationResponse>(
    `/models/${modelId}/explain-chart`,
    data
  );
  return response.data;
};

/**
 * Explain model benchmarks using AI.
 */
export interface BenchmarkExplanationRequest {
  chart_type: string;
  comparison_data: any;
  model_ids: string[];
}

export const explainBenchmarks = async (
  data: BenchmarkExplanationRequest
): Promise<ChartExplanationResponse> => {
  const response = await apiClient.post<ChartExplanationResponse>(
    '/models/benchmarks/explain',
    data
  );
  return response.data;
};

/**
 * Validation License and Model Download API.
 */
export interface DownloadStatusResponse {
  state: 'ready' | 'needs_acceptance' | 'unavailable' | 'blocked';
  reason?: string;
  license_version?: string;
}

export interface CurrentLicenseResponse {
  version: string;
  sha256: string;
  body: string;
}

export interface AcceptLicensePayload {
  license_version: string;
  license_sha256: string;
  accepting_person_name: string;
  accepting_person_email: string;
  accepting_person_role: 'faculty' | 'clinician' | 'postdoc' | 'research_staff' | 'research_student';
  institution_name: string;
  institution_type: 'academic' | 'hospital' | 'nonprofit_research';
  capacity_confirmed: boolean;
  group_responsible_confirmed: boolean;
  institution_policy_confirmed: boolean;
}

export interface DownloadTokenResponse {
  download_token: string;
  expires_in: number;
}

export const getModelDownloadStatus = async (
  modelId: string
): Promise<DownloadStatusResponse> => {
  const response = await apiClient.get<DownloadStatusResponse>(
    `/models/${modelId}/download/status`
  );
  return response.data;
};

export const getCurrentLicense = async (): Promise<CurrentLicenseResponse> => {
  const response = await apiClient.get<CurrentLicenseResponse>('/license/current');
  return response.data;
};

export const acceptModelLicense = async (
  modelId: string,
  payload: AcceptLicensePayload
): Promise<DownloadTokenResponse> => {
  const response = await apiClient.post<DownloadTokenResponse>(
    `/models/${modelId}/license/accept`,
    payload
  );
  return response.data;
};

export const getModelDownloadToken = async (
  modelId: string
): Promise<DownloadTokenResponse> => {
  const response = await apiClient.post<DownloadTokenResponse>(
    `/models/${modelId}/download/token`
  );
  return response.data;
};

export const getDownloadPackageUrl = (token: string): string => {
  const base = apiClient.defaults.baseURL || 'http://localhost:8000/api/v1';
  let originUrl: string;
  if (base.startsWith('http://') || base.startsWith('https://')) {
    originUrl = base;
  } else if (typeof window !== 'undefined') {
    originUrl = `${window.location.origin}${base.startsWith('/') ? '' : '/'}${base}`;
  } else {
    originUrl = `http://localhost:8000${base.startsWith('/') ? '' : '/'}${base}`;
  }
  const cleanBase = originUrl.replace(/\/+$/, '');
  return `${cleanBase}/download/${token}`;
};

export const triggerFileDownload = (url: string): void => {
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', '');
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};
